import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { VesselSafetyStats } from "../../common/Vessel";
import { FormEvent, useEffect, useState } from "react";
import { useUpdateVesselStatsMutation } from "../../redux/api/vessel";
import { usePrompt } from "../../util/usePrompt";

const AdminStatsForm = (props: {
  parentCallback: Function;
  vesselStats: VesselSafetyStats[];
  formSubmitted: boolean;
  setFormSubmitted: (value: boolean) => void;
  changedRowsWithError: number[];
}) => {
  const [vesselStatsList, setVesselStatsList] = useState<VesselSafetyStats[]>(
    []
  );
  const [changedRows, setChangedRows] = useState<number[]>([]);

  const [saveVesselStats] = useUpdateVesselStatsMutation();

  useEffect(() => {
    const sortedStatsList = [...props.vesselStats]
      .map((stat) => {
        return {
          ...stat,
          nearMissSubmissions: stat.nearMissSubmissions || 0,
          lostTimeInjuries: stat.lostTimeInjuries || 0,
          restrictedWorkInjuries: stat.restrictedWorkInjuries || 0,
          medicalTreatmentInjuries: stat.medicalTreatmentInjuries || 0,
        };
      })
      .sort((a, b) => (a.name < b.name ? -1 : 1));
    setVesselStatsList(sortedStatsList);
  }, [props.vesselStats]);

  usePrompt(
    "Changes will not be saved, would you like to leave?",
    changedRows.length > 0
  );

  const validateStats = (stats: VesselSafetyStats) => {
    if (
      stats.nearMissSubmissions === undefined ||
      stats.nearMissSubmissions === null ||
      stats.nearMissSubmissions < 0 ||
      stats.nearMissSubmissions % 1 !== 0
    ) {
      return false;
    } else if (
      stats.lostTimeInjuries === undefined ||
      stats.lostTimeInjuries === null ||
      stats.lostTimeInjuries < 0 ||
      stats.lostTimeInjuries % 1 !== 0
    ) {
      return false;
    } else if (
      stats.restrictedWorkInjuries === undefined ||
      stats.restrictedWorkInjuries === null ||
      stats.restrictedWorkInjuries < 0 ||
      stats.restrictedWorkInjuries % 1 !== 0
    ) {
      return false;
    } else if (
      stats.medicalTreatmentInjuries === undefined ||
      stats.medicalTreatmentInjuries === null ||
      stats.medicalTreatmentInjuries < 0 ||
      stats.medicalTreatmentInjuries % 1 !== 0
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = (e: FormEvent) => {
    if (props.formSubmitted === true) {
      return;
    }
    props.setFormSubmitted(true);
    e.preventDefault();
    const changedStatRows = [...vesselStatsList].filter((r) => {
      return changedRows.includes(r.id);
    });
    let errors: number[] = [];
    for (var i = 0; i < changedStatRows.length; i++) {
      if (!validateStats(changedStatRows[i])) {
        errors.push(changedStatRows[i].id as number);
      }
    }
    submitForm(changedStatRows, errors);
  };

  async function submitForm(
    vesselStats: VesselSafetyStats[],
    errors: number[]
  ) {
    if (vesselStats.length === 0) {
      props.parentCallback({ noChange: true });
    } else if (errors.length > 0) {
      props.parentCallback({ hasError: true, errors });
    } else {
      let saveResult: {
        data: {
          successCount: number;
          failed: boolean;
        };
        hasData: boolean;
      } = {
        data: { successCount: 0, failed: false },
        hasData: true,
      };
      const response = await saveVesselStats(vesselStats);
      if ("error" in response) {
        saveResult.data.failed = true;
      } else {
        saveResult.data.successCount = changedRows.length;
      }
      props.parentCallback(saveResult);
    }
  }

  const handleStatsUpdate = (
    e: any,
    row: VesselSafetyStats,
    value?: number | undefined
  ) => {
    const tempStats = [...vesselStatsList];
    let idx = tempStats.findIndex((r) => r.id === row.id);
    if (idx !== -1) {
      if (value !== undefined) {
        tempStats[idx] = {
          ...tempStats[idx],
          [e.target.name]: e.target.value as number,
        };
      }
    }
    if (!changedRows.includes(row.id)) {
      let updatedRows = changedRows;
      updatedRows.push(row.id);
      setChangedRows(updatedRows);
    }
    setVesselStatsList(tempStats);
  };

  return (
    <>
      <form id="vesselStatsForm" onSubmit={handleSubmit} />
      <TableContainer
        className="table vesselstat-table"
        style={{ width: "auto" }}
      >
        <Table stickyHeader={true}>
          <TableHead>
            <TableRow>
              <TableCell className="rating">Vessel</TableCell>
              <TableCell className="medium-field required">
                Near Miss Submissions
              </TableCell>
              <TableCell className="medium-field required">
                Lost Time Injuries
              </TableCell>
              <TableCell className="medium-field required">
                Restricted Work Injuries
              </TableCell>
              <TableCell className="medium-field required">
                Medical Treatment Injuries
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vesselStatsList!.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="left rating wd-100">
                  <div>
                    <Tooltip title={row.name} placement="right">
                      <span>{row.name}</span>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell className="center wb-wrap">
                  <TextField
                    value={
                      !!row.nearMissSubmissions ? row.nearMissSubmissions : 0
                    }
                    name="nearMissSubmissions"
                    onChange={(e) =>
                      handleStatsUpdate(
                        e,
                        row,
                        e.target.value
                          ? (e.target.value as unknown as number)
                          : undefined
                      )
                    }
                    type="number"
                    inputProps={{ step: 1, min: 0 }}
                    error={
                      (row.nearMissSubmissions === null ||
                        row.nearMissSubmissions === undefined ||
                        row.nearMissSubmissions < 0 ||
                        row.nearMissSubmissions % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number)
                    }
                    helperText={
                      (row.nearMissSubmissions === null ||
                        row.nearMissSubmissions === undefined ||
                        row.nearMissSubmissions < 0 ||
                        row.nearMissSubmissions % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number) &&
                      "Needs to be a whole number"
                    }
                  />
                </TableCell>
                <TableCell className="center wb-wrap">
                  <TextField
                    value={!!row.lostTimeInjuries ? row.lostTimeInjuries : 0}
                    name="lostTimeInjuries"
                    onChange={(e) =>
                      handleStatsUpdate(
                        e,
                        row,
                        e.target.value
                          ? (e.target.value as unknown as number)
                          : undefined
                      )
                    }
                    type="number"
                    inputProps={{ step: 1, min: 0 }}
                    error={
                      (row.lostTimeInjuries === null ||
                        row.lostTimeInjuries === undefined ||
                        row.lostTimeInjuries < 0 ||
                        row.lostTimeInjuries % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number)
                    }
                    helperText={
                      (row.lostTimeInjuries === null ||
                        row.lostTimeInjuries === undefined ||
                        row.lostTimeInjuries < 0 ||
                        row.lostTimeInjuries % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number) &&
                      "Needs to be a whole number"
                    }
                  />
                </TableCell>
                <TableCell className="center wb-wrap">
                  <TextField
                    value={
                      !!row.restrictedWorkInjuries
                        ? row.restrictedWorkInjuries
                        : 0
                    }
                    name="restrictedWorkInjuries"
                    onChange={(e) =>
                      handleStatsUpdate(
                        e,
                        row,
                        e.target.value
                          ? (e.target.value as unknown as number)
                          : undefined
                      )
                    }
                    type="number"
                    inputProps={{ step: 1, min: 0 }}
                    error={
                      (row.restrictedWorkInjuries === null ||
                        row.restrictedWorkInjuries === undefined ||
                        row.restrictedWorkInjuries < 0 ||
                        row.restrictedWorkInjuries % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number)
                    }
                    helperText={
                      (row.restrictedWorkInjuries === null ||
                        row.restrictedWorkInjuries === undefined ||
                        row.restrictedWorkInjuries < 0 ||
                        row.restrictedWorkInjuries % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number) &&
                      "Needs to be a whole number"
                    }
                  />
                </TableCell>
                <TableCell className="center wb-wrap">
                  <TextField
                    value={
                      !!row.medicalTreatmentInjuries
                        ? row.medicalTreatmentInjuries
                        : 0
                    }
                    name="medicalTreatmentInjuries"
                    onChange={(e) =>
                      handleStatsUpdate(
                        e,
                        row,
                        e.target.value
                          ? (e.target.value as unknown as number)
                          : undefined
                      )
                    }
                    type="number"
                    inputProps={{ step: 1, min: 0 }}
                    error={
                      (row.medicalTreatmentInjuries === null ||
                        row.medicalTreatmentInjuries === undefined ||
                        row.medicalTreatmentInjuries < 0 ||
                        row.medicalTreatmentInjuries % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number)
                    }
                    helperText={
                      (row.medicalTreatmentInjuries === null ||
                        row.medicalTreatmentInjuries === undefined ||
                        row.medicalTreatmentInjuries < 0 ||
                        row.medicalTreatmentInjuries % 1 !== 0) &&
                      props.changedRowsWithError.includes(row.id as number) &&
                      "Needs to be a whole number"
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default AdminStatsForm;
